import type { Reference } from '@apollo/client';
import { useMutation } from '@apollo/client';
import type Quill from 'quill/core';
import { removeReactionThreadFromEditor } from './threadHelpers';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { usePostLayoutApi } from '../PostLayoutProvider';

const ADD_REACTION_FRAGMENT = gql(`
  fragment AddReaction on PublicReactionFields {
    id
    # eslint-disable-next-line slab-graphql/no-unused-fields
    emoji
  }
`);

const ADD_REACTION_MUTATION = gql(`
  mutation addReaction($emoji: String!, $threadId: ID!) {
    addReaction(emoji: $emoji, threadId: $threadId) {
      id
      ...AddReaction
    }
  }
`);

const DELETE_REACTION_MUTATION = gql(`
  mutation deleteReaction($id: ID!) {
    deleteReaction(reactionId: $id) {
      id
    }
  }
`);

export const useAddReaction = () => {
  const [addReaction] = useMutation(ADD_REACTION_MUTATION);

  return (emoji: string, threadId: string) =>
    addReaction({
      variables: { emoji, threadId },
      update: (proxy, { data }) => {
        if (data?.addReaction) {
          proxy.modify({
            id: proxy.identify({ __typename: 'Thread', id: threadId }),
            fields: {
              reactions: (
                existingReactions: readonly Reference[],
                { readField },
              ) => {
                const newReactionRef = proxy.writeFragment({
                  data: data.addReaction,
                  fragment: ADD_REACTION_FRAGMENT,
                });

                if (
                  newReactionRef == null ||
                  existingReactions.some(
                    (ref) => readField('id', ref) === data.addReaction.id,
                  )
                ) {
                  return [...existingReactions];
                }

                return [newReactionRef, ...existingReactions];
              },
            },
          });
        }
      },
    });
};

const DELETE_REACTION_FRAGMENT = gql(`
  fragment useDeleteReaction_thread on PublicThreadFields {
    id
    reactions {
      id
    }
  }
`);

export const useDeleteReaction = () => {
  const [deleteReaction] = useMutation(DELETE_REACTION_MUTATION);
  const { triggerMagneticWallCollapseAction } = usePostLayoutApi();
  return (
    id: string,
    thread: DocumentType<typeof DELETE_REACTION_FRAGMENT>,
    quill: Quill | null,
  ) => {
    if (quill && thread) {
      if (thread.reactions.length === 1) {
        // only 1 reaction, delete the thread too
        removeReactionThreadFromEditor(quill, thread.id);
        triggerMagneticWallCollapseAction();
      }
    }
    return deleteReaction({
      variables: { id },
      update: (proxy, { data }) => {
        if (data?.deleteReaction) {
          proxy.modify({
            id: proxy.identify({ __typename: 'Thread', id: thread.id }),
            fields: {
              reactions: (existingReactions, { readField }) =>
                existingReactions.filter(
                  (existingReaction: { id: string }) =>
                    readField('id', existingReaction) !==
                    data.deleteReaction.id,
                ),
            },
          });
        }
      },
    });
  };
};
